import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import './Support.css';
import CustomerInfoModal from '../../components/support/CustomerInfoModal';
import ConfirmationModal from '../../components/support/ConfirmationModal';
import JobDetails from '../../components/support/JobDetails';
import TranslatorInfoModal from '../../components/support/TranslatorInfoModal';

const API_URL = process.env.REACT_APP_API_URL;

const SupportPendingJobsPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  
  const [pendingJobs, setPendingJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [fees, setFees] = useState({});
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [eligibleTranslators, setEligibleTranslators] = useState({});
  const [selectedTranslators, setSelectedTranslators] = useState({});
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isTranslatorModalOpen, setIsTranslatorModalOpen] = useState(false);
  const [selectedTranslator, setSelectedTranslator] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [jobDetailsCache, setJobDetailsCache] = useState({});

  const fetchPendingJobs = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true); setError('');
      const [jobsResponse, langsResponse] = await Promise.all([
          fetch(`${API_URL}/api/support/jobs/pending`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/public/languages`)
      ]);
      if (!jobsResponse.ok) throw new Error(t('error_fetch_jobs'));
      if (!langsResponse.ok) throw new Error(t('error_fetch_languages'));
      const jobsData = await jobsResponse.json();
      const langsData = await langsResponse.json();
      setPendingJobs(jobsData);
      setAvailableLanguages(langsData.map(lang => ({ value: lang.code, label: t(`lang_${lang.code}`) })));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchPendingJobs();
  }, [fetchPendingJobs]);
  
  const filteredJobs = useMemo(() => {
    if (!searchTerm) return pendingJobs;
    const lowerCaseSearch = searchTerm.toLowerCase();
    return pendingJobs.filter(job =>
        job.id.toString().includes(lowerCaseSearch) ||
        (job.customer_first_name && `${job.customer_first_name} ${job.customer_last_name}`.toLowerCase().includes(lowerCaseSearch))
    );
  }, [pendingJobs, searchTerm]);

  const toggleDetails = useCallback(async (jobId) => {
    const newExpandedId = expandedJobId === jobId ? null : jobId;
    setExpandedJobId(newExpandedId);
    if (newExpandedId && !jobDetailsCache[jobId] && !eligibleTranslators[jobId]?.loading) {
      try {
        setEligibleTranslators(prev => ({ ...prev, [jobId]: { loading: true, error: null, data: [] } }));
        const [detailsResponse, translatorsResponse] = await Promise.all([
          fetch(`${API_URL}/api/support/jobs/${jobId}/details`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/support/jobs/${jobId}/eligible-translators`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (!detailsResponse.ok || !translatorsResponse.ok) throw new Error('İş detayları veya çevirmenler alınamadı.');
        const detailedJobData = await detailsResponse.json();
        const elData = await translatorsResponse.json();
        setJobDetailsCache(prev => ({ ...prev, [jobId]: detailedJobData }));
        setEligibleTranslators(prev => ({ ...prev, [jobId]: { loading: false, error: null, data: elData } }));
      } catch (err) {
        setEligibleTranslators(prev => ({ ...prev, [jobId]: { loading: false, error: err.message, data: [] } }));
      }
    }
  }, [expandedJobId, token, jobDetailsCache, eligibleTranslators]);

  const handleFeeChange = (jobId, value) => setFees(prev => ({ ...prev, [jobId]: value }));
  const handleApproveJob = async (jobId) => {
    const translatorFee = fees[jobId];
    if (!translatorFee || isNaN(translatorFee) || translatorFee <= 0) { alert(t('alert_enter_valid_fee')); return; }
    try {
      const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ translatorFee }) });
      if (!response.ok) { const data = await response.json(); throw new Error(t(data.messageKey) || t('error_job_approval')); }
      alert(t('success_job_approved'));
      fetchPendingJobs();
    } catch (err) { alert(err.message); }
  };
  
  const handleAssignJob = async (jobId) => {
    const translatorId = selectedTranslators[jobId];
    const translatorFee = fees[jobId];
    if (!translatorId || !translatorFee) { alert(t('alert_select_translator_and_fee')); return; }
    try {
      const response = await fetch(`${API_URL}/api/support/jobs/${jobId}/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ translatorId, translatorFee }) });
      if (!response.ok) { const data = await response.json(); throw new Error(t(data.messageKey) || t('error_job_assignment')); }
      alert(t('success_job_assigned'));
	    fetchPendingJobs();
    } catch (err) { alert(err.message); }
  };

  const openCancelModal = (job) => { setJobToCancel(job); setIsCancelModalOpen(true); };
  const handleCancelJob = async () => {
    if (!jobToCancel) return;
    try {
      const response = await fetch(`${API_URL}/api/support/jobs/${jobToCancel.id}/cancel`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(t('error_job_cancel'));
      alert(t('success_job_cancelled'));
	    fetchPendingJobs();
      setIsCancelModalOpen(false);
      setJobToCancel(null);
    } catch (err) { alert(err.message); }
  };

  const handleCustomerNameClick = (job) => {
    setSelectedCustomer({
      first_name: job.customer_first_name, last_name: job.customer_last_name,
      email: job.customer_email, phone_code: job.customer_phone_code, 
      phone_number: job.customer_phone_number, total_orders: job.customer_total_orders
    });
    setIsCustomerModalOpen(true);
  };
  
  const handleJobUpdate = (updatedJob) => {
    setJobDetailsCache(prev => ({ ...prev, [updatedJob.id]: updatedJob }));
    setPendingJobs(prev => prev.map(j => j.id === updatedJob.id ? { ...j, ...updatedJob } : j));
  };
  const handleNewFileAdded = (jobId, newFileData) => {
    const updateJobs = (jobs) => jobs.map(job => {
        if (job.id === jobId) {
            const updatedFiles = [...(job.files || []), newFileData];
            const updatedWordCount = (job.word_count || 0) + (newFileData.word_count || 0);
            return { ...job, files: updatedFiles, word_count: updatedWordCount };
        }
        return job;
    });
    setPendingJobs(prev => updateJobs(prev));
    setJobDetailsCache(prev => ({...prev, [jobId]: updateJobs([prev[jobId] || {}])[0] }));
  };
  const handleFileDeleted = (jobId, deletedFileId, wordCountChange) => {
    const updateJobs = (jobs) => jobs.map(job => {
        if (job.id === jobId) {
            const updatedFiles = job.files.filter(file => file.id !== deletedFileId);
            const updatedWordCount = Math.max(0, (job.word_count || 0) - (wordCountChange || 0));
            return { ...job, files: updatedFiles, word_count: updatedWordCount };
        }
        return job;
    });
    setPendingJobs(prev => updateJobs(prev));
    setJobDetailsCache(prev => ({...prev, [jobId]: updateJobs([prev[jobId] || {}])[0] }));
  };

  const renderContent = () => {
    if (loading) return <div className="table-row-message"><i className="fas fa-spinner fa-spin"></i> {t('loading_jobs')}</div>;
    if (error) return <div className="table-row-message error">{error}</div>;
    if (filteredJobs.length === 0) return <div className="table-row-message">{t('no_pending_jobs_found')}</div>;

    return (
        <div className="pending-jobs-list">
            {filteredJobs.map(job => {
              const detailedJobData = jobDetailsCache[job.id] || job;
              const detailedJob = { ...detailedJobData, files: detailedJobData.files || [] };
              const targetLangsArray = Array.isArray(job.target_langs) ? job.target_langs : [];
              return (
                  <div className={`job-card ${expandedJobId === job.id ? 'expanded' : ''}`} key={job.id}>
                    <div className="job-card-header" onClick={() => toggleDetails(job.id)}>
                      <div className="job-info">
                        <h3>#{job.id} - {job.source_lang.toUpperCase()} → {targetLangsArray.join(', ').toUpperCase()}</h3>
                        <button className="customer-name-btn" onClick={(e) => { e.stopPropagation(); handleCustomerNameClick(job); }}>
                          <i className="fas fa-user"></i> {job.customer_first_name} {job.customer_last_name}
                        </button>
                      </div>
                      <div className="header-actions">
                        <span className="job-price-display">{t('customer_price')}: <strong>{parseFloat(job.price).toLocaleString(i18n.language, {style: 'currency', currency: 'TRY'})}</strong></span>
                        <i className={`fas fa-chevron-down details-chevron ${expandedJobId === job.id ? 'open' : ''}`}></i>
                      </div>
                    </div>
                    {expandedJobId === job.id && 
                      <JobDetails 
                        job={detailedJob} t={t} onJobUpdate={handleJobUpdate}
                        onNewFileAdded={(newFile) => handleNewFileAdded(job.id, newFile)} 
                        onFileDeleted={(fileId, wordCount) => handleFileDeleted(job.id, fileId, wordCount)}
                        eligibleTranslators={eligibleTranslators[job.id] || { loading: true, error: null, data: [] }}
                        selectedTranslator={selectedTranslators[job.id]}
                        onTranslatorSelect={(translatorId) => setSelectedTranslators(p => ({ ...p, [job.id]: translatorId }))}
                        onAssign={() => handleAssignJob(job.id)}
                        fees={fees} onFeeChange={(value) => handleFeeChange(job.id, value)}
                        availableLanguages={availableLanguages}
                      />}
                    <div className="job-card-actions">
                        <button className="cancel-btn" onClick={(e) => {e.stopPropagation(); openCancelModal(job);}}><i className="fas fa-times-circle"></i> {t('cancel_job')}</button>
                        <div className="approval-group">
                            <div className="fee-input-group">
                                <label htmlFor={`fee-${job.id}`}>{t('translator_fee')}</label>
                                <input type="number" step="0.01" id={`fee-${job.id}`} value={fees[job.id] || ''} onChange={(e) => handleFeeChange(job.id, e.target.value)} onClick={(e) => e.stopPropagation()} placeholder="0.00" />
                                <span>(TRY)</span>
                            </div>
                            <button onClick={(e) => {e.stopPropagation(); handleApproveJob(job.id);}} className="approve-btn" disabled={!fees[job.id]}><i className="fas fa-check"></i> {t('approve_and_publish_btn')}</button>
                        </div>
                    </div>
                  </div>
                );
            })}
        </div>
    );
  };

  return (
    <>
      <div className="page-container admin-dashboard">
        <div className="support-page-header">
            <h1 className="support-page-title">{t('pending_approval')}</h1>
            <div className="support-filters-compact">
                <input type="text" placeholder={t('filter_by_id_or_name')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
        </div>
        <div className="account-content-card">
            {renderContent()}
        </div>
      </div>
      <CustomerInfoModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} customer={selectedCustomer} />
      <ConfirmationModal isOpen={isCancelModalOpen} onClose={() => setIsCancelModalOpen(false)} onConfirm={handleCancelJob} title={t('confirm_cancel_job_title')} message={t('confirm_cancel_job_message', { jobId: jobToCancel?.id })} />
	  <TranslatorInfoModal isOpen={isTranslatorModalOpen} onClose={() => setIsTranslatorModalOpen(false)} translator={selectedTranslator} />
	</>
  );
};

export default SupportPendingJobsPage;